import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router";
import axios from "axios";
import Error from "../Component/Error";
import Loader from "../Component/Loader";
import config from "../config";
import "./PGHome.css";

function PGHome() {
  const { randKey } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [phonePeUrl, setPhonePeUrl] = useState(null);
  const [countdown, setCountdown] = useState(10);
  const [showRedirect, setShowRedirect] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);

  const countdownRef = useRef(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const returnFromPayment = urlParams.get("returnFromPayment");
    const txn = urlParams.get("txn");

    if (returnFromPayment && txn) {
      setTransactionId(txn);
      setIsRedirecting(false);
      initializePaymentForReturn(txn);
    } else {
      initializePayment();
    }
  }, [randKey, location.search]);

  useEffect(() => {
    if (countdown === 0 && showRedirect && !isRedirecting && phonePeUrl) {
      setTimeout(() => {
        redirectToPhonePe();
      }, 100);
    }
  }, [countdown, showRedirect, isRedirecting, phonePeUrl]);

  const initializePaymentForReturn = async (txnId) => {
    try {
      setLoading(true);
      setPollingCount(0);

      const campId = randKey.slice(0, 2);

      const campaignResponse = await axios.get(
        `${config.API_URL}/payment/campaign/${campId}/${randKey}`
      );

      const statusResponse = await axios.get(
        `${config.API_URL}/payment/status/${txnId}`
      );

      if (
        statusResponse.data.status === "200" &&
        campaignResponse.data.status === "200"
      ) {
        const statusData = statusResponse.data.data;
        const campaignData = campaignResponse.data.data;

        const combinedData = {
          customerName: statusData.customerName,
          productType: statusData.productType,
          principalName: campaignData.principalName,
          amount: statusData.amount,
          companyLogo: campaignData.companyLogo,
          backgroundColor: campaignData.backgroundColor,
          termsUrl: campaignData.termsUrl || "#",
          privacyUrl: campaignData.privacyUrl || "#",
          footerText: campaignData.footerText,
          footerStyle: campaignData.footerStyle,
          footerBackgroundColor: campaignData.footerBackgroundColor,
        };

        setPaymentData(combinedData);

        setTransactionId(txnId);
        setShowRedirect(false);

        const status = statusData.paymentStatus;
        if (status === "COMPLETED") {
          setPaymentStatus("success");
        } else if (status === "FAILED") {
          setPaymentStatus("failed");
        } else {
          setPaymentStatus("pending");
          setTimeout(() => {
            checkPaymentStatus(txnId);
          }, 100);
        }
      } else {
        setError("Failed to get payment or campaign data");
      }
    } catch (err) {
      setError("Failed to get payment status. Please try again.");
      console.error("Payment status error:", err);
    } finally {
      setLoading(false);
    }
  };

  const initializePayment = async () => {
    try {
      setLoading(true);
      setPollingCount(0);
      const response = await axios.post(`${config.API_URL}/payment/initiate`, {
        randKey: randKey,
      });

      if (response.data.status === "200") {
        const data = response.data.data;
        setPaymentData(data);
        setTransactionId(data.transactionId);
        const paymentUrl = data?.paymentUrl;

        if (paymentUrl) {
          setPhonePeUrl(paymentUrl);

          const currentStatus = data.paymentStatus || "PENDING";

          if (currentStatus === "COMPLETED") {
            setPaymentStatus("success");
            setShowRedirect(false);
          } else if (currentStatus === "FAILED") {
            setPaymentStatus("failed");
            setShowRedirect(false);
          } else {
            setShowRedirect(true);
            setTimeout(() => {
              startCountdown();
            }, 100);
          }
        }
      } else {
        setError(response.data.message || "Failed to initialize payment");
      }
    } catch (err) {
      setError("Payment initialization failed. Please try again.");
      console.error("Payment init error:", err);
    } finally {
      setLoading(false);
      setIsRetrying(false);
    }
  };

  const startCountdown = () => {
    setCountdown(10);
    setIsRedirecting(false);

    // Clear any existing interval
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setTimeout(() => {
            redirectToPhonePe();
          }, 100);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const redirectToPhonePe = () => {
    if (isRedirecting) {
      return;
    }

    if (!phonePeUrl) {
      return;
    }

    setIsRedirecting(true);

    try {
      window.location.href = phonePeUrl;
    } catch (error) {
      console.error("Error redirecting to PhonePe:", error);
      setIsRedirecting(false);
    }
  };

  const checkPaymentStatus = async (txnId = null) => {
    try {
      const idToCheck = txnId || transactionId;
      setIsCheckingStatus(true);

      const response = await axios.get(
        `${config.API_URL}/payment/status/${idToCheck}`
      );

      if (response.data.status === "200") {
        const status = response.data.data.paymentStatus;

        if (status === "COMPLETED") {
          setPaymentStatus("success");
          setIsRedirecting(false);
          setShowRedirect(false);
          setPollingCount(0);
        } else if (status === "FAILED") {
          setPaymentStatus("failed");
          setIsRedirecting(false);
          setShowRedirect(false);
          setPollingCount(0);
        } else {
          setPaymentStatus("pending");

          if (pollingCount >= 20) {
            setPaymentStatus("failed");
            setIsRedirecting(false);
            setShowRedirect(false);
            setPollingCount(0);
            return;
          }

          setPollingCount((prev) => prev + 1);
          setTimeout(() => {
            checkPaymentStatus(idToCheck);
          }, 3000);
        }
      } else {
        setPaymentStatus("failed");
        setIsRedirecting(false);
        setShowRedirect(false);
        setPollingCount(0);
      }
    } catch (err) {
      console.error("Error checking payment status:", err);
      setPaymentStatus("failed");
      setIsRedirecting(false);
      setShowRedirect(false);
      setPollingCount(0);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  useEffect(() => {
    if (isRedirecting) {
      const timeout = setTimeout(() => {
        setIsRedirecting(false);
      }, 300000);

      return () => clearTimeout(timeout);
    }
  }, [isRedirecting]);

  const handleManualRedirect = () => {
    if (phonePeUrl && !isRedirecting) {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      setCountdown(0);
      setIsRedirecting(false);
      redirectToPhonePe();
    } else if (isRedirecting) {
      alert("Payment redirect is already in progress. Please wait.");
    }
  };

  const resetPaymentState = () => {
    setPaymentStatus("pending");
    setShowRedirect(false);
    setIsRedirecting(false);
    setIsCheckingStatus(false);
    setIsRetrying(true);
    setPollingCount(0);

    setTransactionId(null);
    setPhonePeUrl(null);

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    initializePayment();
  };

  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <Error msg={error} />;
  }

  return (
    <div className="phonepe-payment-page">
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `,
        }}
      />
      <div
        className="phonepe-payment-header"
        style={{
          background: paymentData?.backgroundColor
            ? `linear-gradient(135deg, ${paymentData.backgroundColor}, ${paymentData.backgroundColor}dd)`
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <div className="phonepe-container">
          <div className="phonepe-row phonepe-align-items-center">
            <div className="phonepe-col-md-8">
              <img
                src={
                  paymentData?.companyLogo || "/assets/images/default-logo.png"
                }
                alt="Company Logo"
                className="phonepe-company-logo"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="phonepe-container phonepe-py-4">
        <div className="phonepe-row phonepe-justify-content-center">
          <div className="phonepe-col-lg-10">
            <div className="phonepe-row phonepe-mb-4">
              <div className="phonepe-col-md-6">
                <div className="phonepe-payment-card phonepe-h-100">
                  <div className="phonepe-card-header">
                    <h3 className="phonepe-text-center phonepe-fw-bold phonepe-mb-0">
                      <span className="phonepe-icon-receipt"></span>
                      Payment Details
                    </h3>
                  </div>
                  <div className="phonepe-card-body">
                    <div className="phonepe-row">
                      <div className="phonepe-col-12">
                        <div className="phonepe-payment-detail-item">
                          <label className="phonepe-payment-label">
                            Customer Name
                          </label>
                          <div className="phonepe-payment-value">
                            {paymentData?.customerName}
                          </div>
                        </div>
                        <div className="phonepe-payment-detail-item">
                          <label className="phonepe-payment-label">
                            Product Type
                          </label>
                          <div className="phonepe-payment-value">
                            {paymentData?.productType}
                          </div>
                        </div>
                        <div className="phonepe-payment-detail-item">
                          <label className="phonepe-payment-label">
                            Principal
                          </label>
                          <div className="phonepe-payment-value">
                            {paymentData?.principalName}
                          </div>
                        </div>
                        <div className="phonepe-payment-detail-item">
                          <label className="phonepe-payment-label">
                            Amount
                          </label>
                          <div className="phonepe-payment-amount">
                            ₹{paymentData?.amount}
                          </div>
                        </div>
                        <div className="phonepe-payment-detail-item">
                          <label className="phonepe-payment-label">
                            Transaction ID
                          </label>
                          <div className="phonepe-payment-value phonepe-text-muted">
                            {transactionId}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="phonepe-col-md-6">
                <div className="phonepe-payment-card phonepe-h-100">
                  <div className="phonepe-card-header">
                    <h4 className="phonepe-text-center phonepe-fw-bold phonepe-mb-0">
                      <span className="phonepe-icon-lock-green"></span>
                      Secure Payment Gateway
                    </h4>
                  </div>
                  <div className="phonepe-card-body-compact">
                    {/* Countdown Section */}
                    {showRedirect && countdown > 0 && !isRedirecting && (
                      <div className="phonepe-text-center phonepe-p-4">
                        <div className="phonepe-countdown-container">
                          <div
                            className="phonepe-countdown-circle"
                            style={{
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                              width: "120px",
                              height: "120px",
                              borderRadius: "50%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto 1.5rem",
                              animation: "pulse 2s infinite",
                            }}
                          >
                            <div className="phonepe-countdown-number">
                              {countdown}
                            </div>
                            <div className="phonepe-countdown-label">
                              seconds
                            </div>
                          </div>
                        </div>
                        <h4 className="phonepe-text-primary phonepe-fw-bold phonepe-mb-3">
                          Redirecting to PhonePe Payment Gateway
                        </h4>
                        <p className="phonepe-text-muted phonepe-fs-5 phonepe-mb-4">
                          You will be redirected to PhonePe's secure payment
                          gateway in{" "}
                          <strong className="phonepe-text-primary">
                            {countdown}
                          </strong>{" "}
                          seconds.
                          <br />
                          Please complete your payment on the PhonePe page.
                        </p>
                        <div className="phonepe-alert-info phonepe-mb-4">
                          <span className="phonepe-icon-info"></span>
                          <strong>Important:</strong> Please do not close this
                          page. You will be redirected back here after payment
                          completion.
                        </div>
                        <button
                          className="phonepe-btn-primary phonepe-mt-4"
                          onClick={handleManualRedirect}
                        >
                          <span className="phonepe-icon-external"></span>
                          Go to Payment Gateway Now
                        </button>
                      </div>
                    )}

                    {/* Redirecting State */}
                    {isRedirecting && (
                      <div className="phonepe-text-center phonepe-p-4">
                        <Loader
                          inline={true}
                          size="large"
                          text="Opening PhonePe Payment Gateway"
                          color="primary"
                        />
                        <p className="phonepe-text-muted phonepe-fs-5 phonepe-mb-4">
                          Redirecting to PhonePe's secure payment gateway...
                          <br />
                          You will be redirected back here after payment
                          completion.
                        </p>
                        <div className="phonepe-alert-info phonepe-mb-4">
                          <span className="phonepe-icon-info"></span>
                          <strong>Note:</strong> If the redirect didn't work,
                          please click the button below.
                        </div>
                        <button
                          className="phonepe-btn-primary phonepe-mt-3"
                          onClick={handleManualRedirect}
                        >
                          <span className="phonepe-icon-external"></span>
                          Go to Payment Gateway Again
                        </button>
                      </div>
                    )}

                    {/* Pending Status */}
                    {paymentStatus === "pending" && !isRedirecting && (
                      <div className="phonepe-text-center phonepe-p-4">
                        {isCheckingStatus ? (
                          <Loader
                            inline={true}
                            size="medium"
                            text="Checking Payment Status..."
                            color="primary"
                          />
                        ) : (
                          <>
                            <Loader
                              inline={true}
                              size="large"
                              text="Payment Status: Pending"
                              color="warning"
                            />
                            <p className="phonepe-text-muted phonepe-fs-5 phonepe-mb-4">
                              Your payment is being processed. Please wait while
                              we confirm the transaction.
                              <br />
                              This may take a few moments.
                            </p>
                            <div className="phonepe-alert-warning phonepe-mb-4">
                              <span className="phonepe-icon-clock"></span>
                              <strong>Processing:</strong> We're checking your
                              payment status. Please don't close this page.
                            </div>
                            <button
                              className="phonepe-btn-warning phonepe-mt-3"
                              onClick={() => checkPaymentStatus(transactionId)}
                            >
                              <span className="phonepe-icon-sync"></span>
                              Check Status Again
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Success Status */}
                    {paymentStatus === "success" && (
                      <div className="phonepe-text-center phonepe-p-4">
                        <div className="phonepe-success-animation phonepe-mb-4">
                          <div className="phonepe-success-circle">
                            <span
                              className="phonepe-icon-check phonepe-text-white phonepe-animated-tick"
                              style={{ fontSize: "3rem" }}
                            ></span>
                          </div>
                        </div>
                        <h3 className="phonepe-text-success phonepe-fw-bold phonepe-mb-3">
                          Payment Successful!
                        </h3>
                        <p className="phonepe-text-muted phonepe-fs-5 phonepe-mb-4">
                          Your payment of{" "}
                          <strong className="phonepe-text-success">
                            ₹{paymentData?.amount}
                          </strong>{" "}
                          has been processed successfully.
                        </p>
                        <div className="phonepe-transaction-details">
                          <span className="phonepe-text-muted phonepe-d-block phonepe-mb-2 phonepe-text-uppercase phonepe-fw-bold phonepe-small">
                            Transaction ID
                          </span>
                          <div className="phonepe-transaction-id-container">
                            <div className="phonepe-fw-bold phonepe-fs-6 phonepe-text-success phonepe-transaction-id">
                              {transactionId}
                            </div>
                            <button
                              className="phonepe-copy-btn"
                              onClick={() => {
                                navigator.clipboard.writeText(transactionId);
                                // Show temporary success message
                                const copyBtn =
                                  document.querySelector(".phonepe-copy-btn");
                                if (copyBtn) {
                                  const originalText = copyBtn.innerHTML;
                                  copyBtn.innerHTML =
                                    '<span className="phonepe-icon-check"></span> Copied!';
                                  copyBtn.classList.add("phonepe-copied");
                                  setTimeout(() => {
                                    copyBtn.innerHTML = originalText;
                                    copyBtn.classList.remove("phonepe-copied");
                                  }, 2000);
                                }
                              }}
                              title="Copy Transaction ID"
                            >
                              <span className="phonepe-icon-copy"></span>
                              Copy
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Failed Status */}
                    {paymentStatus === "failed" && (
                      <div className="phonepe-text-center phonepe-p-4">
                        <div className="phonepe-error-animation phonepe-mb-4">
                          <div className="phonepe-error-circle">
                            <span
                              className="phonepe-icon-times phonepe-text-white"
                              style={{ fontSize: "3rem" }}
                            ></span>
                          </div>
                        </div>
                        <h3 className="phonepe-text-danger phonepe-fw-bold phonepe-mb-3">
                          Payment Failed
                        </h3>
                        <p className="phonepe-text-muted phonepe-fs-5 phonepe-mb-4">
                          We couldn't process your payment. Please try again or
                          contact support.
                        </p>
                        <div className="phonepe-d-flex phonepe-justify-content-center phonepe-gap-3">
                          <button
                            className="phonepe-btn-primary"
                            onClick={resetPaymentState}
                            disabled={isRetrying}
                          >
                            {isRetrying ? (
                              <>
                                <Loader
                                  inline={true}
                                  size="small"
                                  color="white"
                                />
                                <span className="phonepe-ml-2">
                                  Retrying...
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="phonepe-icon-redo"></span>
                                Try Again
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Retrying State */}
                    {isRetrying && (
                      <div className="phonepe-text-center phonepe-p-4">
                        <Loader
                          inline={true}
                          size="large"
                          text="Reinitializing Payment..."
                          color="primary"
                        />
                        <p className="phonepe-text-muted phonepe-fs-5 phonepe-mb-4">
                          Setting up a new payment session for you.
                          <br />
                          Please wait while we prepare the payment gateway.
                        </p>
                        <div className="phonepe-alert-info phonepe-mb-4">
                          <span className="phonepe-icon-info"></span>
                          <strong>Please wait:</strong> We're creating a fresh
                          payment session to ensure security.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Footer with Dynamic Links - Full Width */}
      <footer
        className="phonepe-payment-footer"
        style={{
          background: paymentData?.footerBackgroundColor
            ? paymentData.footerBackgroundColor
            : "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
        }}
      >
        <div className="phonepe-container">
          {/* Footer Links Section */}
          <div className="phonepe-row">
            <div className="phonepe-col-12">
              <div>
                <div className="phonepe-footer-links-row">
                  <h6
                    className="phonepe-footer-title"
                    style={{
                      fontSize: paymentData.footerStyle?.fontSize,
                      color: paymentData.footerStyle?.color,
                    }}
                  >
                    Legal Information
                  </h6>
                  <div className="phonepe-footer-links">
                    <a
                      href={paymentData?.termsUrl || "#"}
                      className="phonepe-footer-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: paymentData.footerStyle?.fontSize,
                        color: paymentData.footerStyle?.color,
                      }}
                    >
                      <span className="phonepe-icon-file-text"></span>
                      Terms & Conditions
                    </a>
                    <a
                      href={paymentData?.privacyUrl || "#"}
                      className="phonepe-footer-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: paymentData.footerStyle?.fontSize,
                        color: paymentData.footerStyle?.color,
                      }}
                    >
                      <span className="phonepe-icon-shield"></span>
                      Privacy Policy
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr className="phonepe-footer-divider" />

          {/* Dynamic Footer Content */}
          {paymentData?.footerText && (
            <div>
              <div
                className="phonepe-footer-text-content"
                dangerouslySetInnerHTML={{
                  __html: paymentData.footerText,
                }}
                style={{
                  fontSize: paymentData.footerStyle?.fontSize || "14px",
                  color: paymentData.footerStyle?.color || "#666666",
                }}
              />
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

export default PGHome;
